import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { sellBoostFetch } from "../../utlifyFunction";
import {
	Card,
	Descriptions,
	List,
	Row,
	Col,
	Typography,
	Tag,
	Space,
	Button,
	Modal,
	Input,
	Checkbox,
	Spin,
} from "antd";
import PrimaryButton from "../../components/Buttons/PrimaryButton";
import AIConfiguration from "../../components/Cards/AIConfiguration";
import { toast } from "react-toastify";

export default function OrderById() {
	const { id } = useParams();
	const [orderData, setOrderData] = useState(null);
	const [loading, setLoading] = useState(false);
	const [modalVisible, setModalVisible] = useState(false);
	const [selectedProduct, setSelectedProduct] = useState(null);
	const [showConfig, setShowConfig] = useState(false);
	const [tone, setTone] = useState("Professional");
	const [maxWords, setMaxWords] = useState(200);
	const [targetAudience, setTargetAudience] = useState("");
	const [productType, setProductType] = useState("Other");
	const [ctaType, setCtaType] = useState("Buy Now");

	const fetchOrderData = async () => {
		try {
			setLoading(true);
			const data = await sellBoostFetch("GET", `/user/order-data/${id}`);
			setOrderData(data?.order);
		} catch (error) {
			console.error("Error fetching order data:", error);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchOrderData();
	}, [id]);

	const getStatusColor = (status) => {
		const statusColors = {
			1: "blue",
			2: "gold",
			3: "cyan",
			4: "geekblue",
		};
		return statusColors[status] || "default";
	};

	const statusMap = {
		1: "Pending",
		2: "Processing",
		3: "Completed",
		4: "Updated",
	};

	const handleRegenerateDescription = async (productId) => {
		try {
			setLoading(true);
			const data = await sellBoostFetch("POST", `/AI/re-generate/${id}`, {
				productId: productId,
				tweakConfiguration: showConfig
					? {
							tone,
							maxWords,
							targetAudience,
							productType,
							ctaType,
					  }
					: false,
			});
			if (data?.status === 200) {
				toast.success("Product description updated!");
				setModalVisible(false);
				fetchOrderData();
			}
		} catch (error) {
			toast.success("Something went wrong in re-generation!");
			console.error("Error ", error);
		} finally {
			setLoading(false);
		}
	};

	const handleUpdateDescription = async (productId) => {
		setLoading(true);
		try {
			const response = await sellBoostFetch(
				"POST",
				`/shopify/update-order/${id}`,
				{
					productId: productId,
				}
			);
			if (response?.status === 200) {
				toast.success("Product description updated!");
				setModalVisible(false);
				fetchOrderData();
			}
		} catch (error) {
			toast.error("Failed to update product description.");
		} finally {
			setLoading(false);
		}
	};

	const renderProductInfo = (item) => {
		if (item.title && item.image && item.oldDescription) {
			return (
				<Row gutter={16} align="middle">
					<Col>
						<img
							src={item.image}
							alt={item.name}
							style={{
								width: 80,
								height: 80,
								objectFit: "cover",
							}}
						/>
					</Col>
					<Col>
						<Typography.Text strong>{item.title}</Typography.Text>
						<br />
					</Col>
				</Row>
			);
		}
		return (
			<Typography.Text strong>Product ID: {item?.productId}</Typography.Text>
		);
	};

	const renderProductActions = (item) => {
		if (item.newDescription) {
			return (
				<Space>
					{!item?.updated && (
						<PrimaryButton
							type="primary"
							size="small"
							height="30px"
							onClick={() => {
								setSelectedProduct(item);
								setModalVisible(true);
							}}
						>
							Review and Update
						</PrimaryButton>
					)}
					<Tag color="success">{item?.updated ? "Updated" : "Completed"}</Tag>
				</Space>
			);
		}
		return <Tag color="processing">Processing Description</Tag>;
	};

	return (
		<div >
			<Typography.Title level={4} style={{ margin: "10px 0px" }}>
				<span
					onClick={() => window.history.back()}
					style={{ cursor: "pointer", marginRight: "8px" }}
				>
					←
				</span>
				Order Details
			</Typography.Title>
			<Card>
				<Spin spinning={loading}>
					<Descriptions title="Order Information" bordered>
						<Descriptions.Item label="Order ID">
							{orderData?._id}
						</Descriptions.Item>
						<Descriptions.Item label="Created At">
							{orderData?.createdAt &&
								new Date(orderData.createdAt).toLocaleDateString("en-US", {
									year: "numeric",
									month: "short",
									day: "numeric",
									hour: "2-digit",
									minute: "2-digit",
								})}
						</Descriptions.Item>
						<Descriptions.Item label="Status">
							<Tag color={getStatusColor(orderData?.orderStatus)}>
								{statusMap[orderData?.orderStatus]}
							</Tag>
						</Descriptions.Item>
					</Descriptions>

					<Typography.Title level={5} style={{ margin: "24px 0 12px" }}>
						Products
					</Typography.Title>
					<List
						bordered
						dataSource={orderData?.orderData || []}
						renderItem={(item) => (
							<List.Item>
								<Row
									style={{ width: "100%" }}
									justify="space-between"
									align="middle"
								>
									<Col span={16}>{renderProductInfo(item)}</Col>
									<Col span={8} style={{ textAlign: "right" }}>
										{renderProductActions(item)}
									</Col>
								</Row>
							</List.Item>
						)}
					/>
				</Spin>
			</Card>

			<Modal
				title="Review and Update Description"
				open={modalVisible}
				onCancel={() => setModalVisible(false)}
				width={800}
				footer={[
					<Button key="cancel" onClick={() => setModalVisible(false)}>
						Cancel
					</Button>,
					<Button
						key="regenerate"
						type="default"
						onClick={() =>
							handleRegenerateDescription(selectedProduct?.productId)
						}
					>
						Regenerate Description
					</Button>,
					<Button
						key="update"
						type="primary"
						onClick={() =>
							handleUpdateDescription(
								selectedProduct?.productId,
								selectedProduct?.newDescription
							)
						}
					>
						Update Description
					</Button>,
				]}
			>
				<Spin spinning={loading}>
					{selectedProduct && (
						<div>
							<div style={{ marginBottom: 24 }}>
								<Row gutter={16} align="middle">
									<Col>
										<img
											src={selectedProduct.image}
											alt={selectedProduct.title}
											style={{
												width: 100,
												height: 100,
												objectFit: "cover",
												borderRadius: "4px",
											}}
										/>
									</Col>
									<Col>
										<Typography.Title level={4} style={{ margin: 0 }}>
											{selectedProduct.title}
										</Typography.Title>
										<Typography.Text type="secondary">
											Product ID: {selectedProduct.productId}
										</Typography.Text>
									</Col>
								</Row>
							</div>
							<div style={{ marginBottom: 24 }}>
								<Typography.Title level={5}>
									Current Description
								</Typography.Title>
								<Typography.Paragraph
									style={{
										background: "#f5f5f5",
										padding: "16px",
										borderRadius: "4px",
									}}
								>
									{selectedProduct.oldDescription}
								</Typography.Paragraph>
							</div>
							<div style={{ marginBottom: 24 }}>
								<Row align="middle" justify="space-between">
									<Typography.Title level={5}>New Description</Typography.Title>
									<Checkbox onChange={(e) => setShowConfig(e.target.checked)}>
										Tweak AI Configuration For Regeneration
									</Checkbox>
								</Row>
								{showConfig && (
									<div style={{ marginBottom: 16 }}>
										<AIConfiguration
											tone={tone}
											setTone={setTone}
											maxWords={maxWords}
											setMaxWords={setMaxWords}
											targetAudience={targetAudience}
											setTargetAudience={setTargetAudience}
											productType={productType}
											setProductType={setProductType}
											ctaType={ctaType}
											setCtaType={setCtaType}
										/>
									</div>
								)}
								<Input.TextArea
									value={selectedProduct.newDescription}
									onChange={(e) =>
										setSelectedProduct({
											...selectedProduct,
											newDescription: e.target.value,
										})
									}
									style={{
										background: "#f0f5ff",
										padding: "16px",
										borderRadius: "4px",
									}}
									rows={6}
									placeholder="Enter new description"
								/>
							</div>
						</div>
					)}
				</Spin>
			</Modal>
		</div>
	);
}
